import { useRouteRef } from '@backstage/core-plugin-api';

import {
  extractLastNameComponent,
  extractWorkflowFromInstanceName,
  formatDatePast,
  useThrottledPrefetch,
} from '@spotify/confidence-core-react';
import {
  AbTestListItemFragment,
  getTypeOrNull,
} from '@spotify/confidence-graphql';
import {
  WorkflowInstanceTableRow,
  workflowInstanceRouteRef,
} from '@spotify/plugin-confidence-workflows';
import { PlanningUnderlineText } from '@spotify/plugin-confidence-workflows-module-planning';
import {
  NameCell,
  OwnerCell,
  StateCell,
  UpdateCell,
  useStateCell,
} from '@spotify/plugin-confidence-workflows-react';

import { useAbTestPrefetch } from '../../hooks';
import { tabForState } from '../AbTestWorkflowInstancePage/utils';
import { ProgressCell } from './ProgressCell';

export function AbTestTableRow({
  workflowInstance,
}: {
  workflowInstance: AbTestListItemFragment;
}) {
  const workflowInstanceRoute = useRouteRef(workflowInstanceRouteRef);
  const href = workflowInstanceRoute({
    workflow: extractWorkflowFromInstanceName(workflowInstance.name)!,
    id: extractLastNameComponent(workflowInstance.name)!,
  });

  // Prefetching the workflow instance when hovering over the row, so that
  // when the user clicks on the row, the workflow instance is already loaded.
  const prefetch = useAbTestPrefetch();
  const { onMouseEnter, onMouseLeave } = useThrottledPrefetch(() =>
    prefetch(workflowInstance.name),
  );

  const tab = tabForState(workflowInstance);
  const stateProps = useStateCell(workflowInstance);

  const secondaryText =
    workflowInstance.state === 'draft' ? (
      `Created ${formatDatePast(new Date(workflowInstance.createTime))}`
    ) : (
      <PlanningUnderlineText planning={workflowInstance.moduleData2.planning} />
    );

  return (
    <WorkflowInstanceTableRow
      workflowInstance={workflowInstance}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      transform={path => `${path}/${tab}`}
    >
      <NameCell
        kind="A/B test"
        displayName={workflowInstance.displayName}
        comments={getTypeOrNull(workflowInstance.comments)}
        secondaryText={secondaryText}
        href={href}
      />
      <StateCell {...stateProps} />
      <ProgressCell workflowInstance={workflowInstance} />
      <UpdateCell updateTime={workflowInstance.updateTime} />
      <OwnerCell owner={getTypeOrNull(workflowInstance.owner)} />
    </WorkflowInstanceTableRow>
  );
}
